import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useSession } from '../lib/session.js';
import { useResource } from '../lib/useResource.js';
import { Logo } from './Logo.js';
import { Modal } from './ui.js';
import { ConviteDeInstalacao } from './Instalar.js';
import { BotaoDeEmergencia } from './Emergencia.js';
import { AtivarAvisos } from './AtivarAvisos.js';
import {
  IconeAgenda,
  IconeAtendimento,
  IconeCatalogo,
  IconeClientes,
  IconeConfiguracoes,
  IconeInicio,
  IconeMais,
  IconeMetricas,
  IconeRetornos,
} from './Icones.js';

interface OverviewCounts {
  needsHumanAttention: unknown[];
}

/**
 * Menu da seção 3, nesta ordem:
 * Visão geral; Atendimento; Clientes e veículos; Produtos e serviços; Agenda;
 * Lembretes e retornos; Configurações.
 *
 * No computador é o menu lateral. No celular, os cinco itens mais usados vão
 * para a barra inferior e o restante fica em "Mais".
 */
const MENU = [
  { to: '/', label: 'Visão geral', curto: 'Início', end: true, permission: 'reports.read', Icone: IconeInicio, naBarra: true },
  { to: '/atendimento', label: 'Atendimento', curto: 'Atender', permission: 'conversations.read', Icone: IconeAtendimento, naBarra: true },
  { to: '/clientes', label: 'Clientes e veículos', curto: 'Clientes', permission: 'customers.read', Icone: IconeClientes, naBarra: true },
  { to: '/catalogo', label: 'Produtos e serviços', curto: 'Produtos', permission: 'catalog.read', Icone: IconeCatalogo, naBarra: false },
  { to: '/agenda', label: 'Agenda', curto: 'Agenda', permission: 'bookings.read', Icone: IconeAgenda, naBarra: true },
  { to: '/lembretes', label: 'Lembretes e retornos', curto: 'Retornos', permission: 'reminders.read', Icone: IconeRetornos, naBarra: true },
  { to: '/metricas', label: 'Métricas', curto: 'Métricas', permission: 'reports.read', Icone: IconeMetricas, naBarra: false },
  { to: '/configuracoes', label: 'Configurações', curto: 'Ajustes', permission: 'company.settings.read', Icone: IconeConfiguracoes, naBarra: false },
];

export function AppShell() {
  const { me, store, can, logout } = useSession();
  const location = useLocation();
  const [maisAberto, setMaisAberto] = useState(false);
  const overview = useResource<OverviewCounts>(can('reports.read') ? '/api/overview' : null, [
    location.pathname,
  ]);
  const emEspera = overview.data?.needsHumanAttention.length ?? 0;

  const visiveis = MENU.filter((item) => can(item.permission));
  const naBarra = visiveis.filter((item) => item.naBarra);
  const emMais = visiveis.filter((item) => !item.naBarra);

  return (
    <>
      {/* Barra superior — só aparece no celular */}
      <header className="topbar">
        <Logo size={32} />
        <div>
          <div className="brand-name">{store?.name ?? 'Auto Center GL'}</div>
        </div>
        <div className="topbar-actions">
          <BotaoDeEmergencia compacto />
          {emEspera > 0 ? (
            <NavLink to="/atendimento" className="badge critical" aria-label={`${emEspera} aguardando atendimento`}>
              {emEspera} em espera
            </NavLink>
          ) : null}
        </div>
      </header>

      {store?.isDemo ? (
        <div className="demo-banner" role="status">
          Modo demonstração — dados fictícios, separados da operação real.
        </div>
      ) : null}

      <div className={store?.isDemo ? 'app com-demo' : 'app'}>
        <aside className="sidebar">
          <div className="brand">
            <Logo />
            <div>
              <div className="brand-name">{store?.name ?? 'Auto Center GL'}</div>
              <div className="brand-sub">Peças · Serviços · Troca de óleo</div>
            </div>
          </div>

          <nav className="nav" aria-label="Menu principal">
            {visiveis.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end}>
                <span>{item.label}</span>
                {item.to === '/atendimento' && emEspera > 0 ? (
                  <span className="nav-count" aria-label={`${emEspera} em espera`}>
                    {emEspera}
                  </span>
                ) : null}
              </NavLink>
            ))}
          </nav>

          <div className="sidebar-footer">
            <div style={{ marginBottom: 12 }}>
              <BotaoDeEmergencia />
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>{me?.name}</strong>
            </div>
            <button type="button" className="small" onClick={() => void logout()}>
              Sair
            </button>
          </div>
        </aside>

        <main className="main">
          <AtivarAvisos />
          <ConviteDeInstalacao />
        <Outlet />
        </main>
      </div>

      {/* Barra inferior — só aparece no celular */}
      <nav className="tabbar" aria-label="Navegação principal">
        {naBarra.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end}>
            <span className="tab-icone">
              <item.Icone />
            </span>
            <span>{item.curto}</span>
            {item.to === '/atendimento' && emEspera > 0 ? (
              <span className="nav-count">{emEspera}</span>
            ) : null}
          </NavLink>
        ))}
        {emMais.length > 0 ? (
          <a
            href="#mais"
            onClick={(e) => {
              e.preventDefault();
              setMaisAberto(true);
            }}
          >
            <span className="tab-icone">
              <IconeMais />
            </span>
            <span>Mais</span>
          </a>
        ) : null}
      </nav>

      {maisAberto ? (
        <Modal title="Mais" onClose={() => setMaisAberto(false)}>
          <nav className="nav" aria-label="Outras seções">
            {emMais.map((item) => (
              <NavLink key={item.to} to={item.to} onClick={() => setMaisAberto(false)}>
                <span className="row" style={{ gap: 10 }}>
                  <item.Icone />
                  {item.label}
                </span>
              </NavLink>
            ))}
          </nav>
          <hr style={{ border: 'none', borderTop: '1px solid var(--gl-border)', margin: '14px 0' }} />
          <div className="stack">
            <div>
              Conectado como <strong>{me?.name}</strong>
            </div>
            <button type="button" onClick={() => void logout()}>
              Sair
            </button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
